import { useEffect, useState } from 'react';

/**
 * Whether the app-wide backup progress toast has been dismissed for the run
 * that is currently in flight.
 *
 * This lives outside the toast because dismissing it used to be a one-way
 * door: `closedForRun` was component state, so once the user closed the toast
 * there was NO way to get it back until the run ended — and a run over a real
 * library is hours long, not minutes. The Cloud Backup page can now offer it
 * back, which means both surfaces need to read and write the same flag.
 *
 * Deliberately NOT Redux and NOT persisted: it is ephemeral per-run UI state,
 * it must never survive a restart, and routing it through a persisted slice
 * would make "hidden" outlive the run it belongs to.
 */
let _hidden = false;
const _listeners = new Set();

function emit() {
    for (const fn of _listeners) fn(_hidden);
}

/** Hide the toast for the remainder of the current run. */
export function hideBackupToast() {
    if (_hidden) return;
    _hidden = true;
    emit();
}

/** Bring it back — from the Cloud Backup page, or when a new run starts. */
export function restoreBackupToast() {
    if (!_hidden) return;
    _hidden = false;
    emit();
}

export function isBackupToastHidden() {
    return _hidden;
}

/** Subscribe to the flag. Returns the current value. */
export function useBackupToastHidden() {
    const [hidden, setHidden] = useState(_hidden);
    useEffect(() => {
        const fn = (v) => setHidden(v);
        _listeners.add(fn);
        // A change between render and subscribe would otherwise be missed.
        setHidden(_hidden);
        return () => { _listeners.delete(fn); };
    }, []);
    return hidden;
}

// ── Restore-wizard visibility ──
// The app-wide toast also carries RESTORE progress, but while the restore
// wizard itself is open the run already has a progress bar front and center —
// showing both would be the same number twice. Same module-level pattern as
// the hidden flag: the wizard writes, the toast reads.
let _wizardOpen = false;
const _wizardListeners = new Set();

export function setRestoreWizardOpen(open) {
    const next = !!open;
    if (_wizardOpen === next) return;
    _wizardOpen = next;
    for (const fn of _wizardListeners) fn(_wizardOpen);
}

/** Subscribe to whether the restore wizard modal is on screen. */
export function useRestoreWizardOpen() {
    const [open, setOpen] = useState(_wizardOpen);
    useEffect(() => {
        const fn = (v) => setOpen(v);
        _wizardListeners.add(fn);
        setOpen(_wizardOpen);
        return () => { _wizardListeners.delete(fn); };
    }, []);
    return open;
}

/** Test seam — resets module state between cases. */
export function _resetBackupToastVisibility() {
    _hidden = false;
    _listeners.clear();
    _wizardOpen = false;
    _wizardListeners.clear();
}
